import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import {
  FolderTree, Plus, Edit, Trash2, Search, Check, X, Loader2, BookOpen
} from 'lucide-react';

const Subjects = () => {
  const user = authService.getCurrentUser();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  
  // Form state
  const [formName, setFormName] = useState('');
  const [saving, setSaving] = useState(false);

  // Check if user can manage subjects
  const canManage = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    setLoading(true);
    try {
      const response = await api.getSubjects();
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      toast.error('Failed to load subjects');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingSubject(null);
    setFormName('');
    setShowAddModal(true);
  };

  const handleEdit = (subject) => {
    setEditingSubject(subject);
    setFormName(subject.name);
    setShowAddModal(true);
  };

  const handleDelete = async (subjectId, subjectName) => {
    if (!window.confirm(`Are you sure you want to delete "${subjectName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.deleteSubject(subjectId);
      toast.success('Subject deleted successfully');
      fetchSubjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete subject');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formName.trim()) {
      toast.error('Subject name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingSubject) {
        await api.updateSubject(editingSubject.id, { name: formName.trim() });
        toast.success('Subject updated successfully');
      } else {
        await api.createSubject({ name: formName.trim() });
        toast.success('Subject created successfully');
      }
      setShowAddModal(false);
      setFormName('');
      setEditingSubject(null);
      fetchSubjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save subject');
    } finally {
      setSaving(false);
    }
  };

  const filteredSubjects = subjects.filter(subject =>
    subject.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="subjects-page" className="max-w-[1200px] mx-auto space-y-4 sm:space-y-6 px-2 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-headings text-slate-900">Subjects</h1>
          <p className="text-sm sm:text-base text-slate-600 font-body mt-1">
            Manage subjects for class delivery and content modules
          </p>
        </div>
        {canManage && (
          <button
            onClick={handleAdd}
            data-testid="add-subject-btn"
            className="w-full sm:w-auto px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2 transition-colors"
          >
            <Plus size={18} />
            Add Subject
          </button>
        )}
      </div>

      {/* Stats Card */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-purple-100 rounded-lg">
            <BookOpen className="text-purple-600" size={24} />
          </div>
          <div>
            <p className="text-3xl font-bold text-slate-900">{subjects.length}</p>
            <p className="text-sm text-slate-500">Total Subjects</p>
          </div>
        </div>
      </div>

      {/* Search and List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {/* Search Bar */}
        <div className="p-4 border-b border-slate-200">
          <div className="relative max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search subjects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              data-testid="subject-search"
            />
          </div>
        </div>

        {/* Subjects List */}
        <div className="divide-y divide-slate-100">
          {filteredSubjects.length === 0 ? (
            <div className="py-12 text-center text-slate-500">
              <FolderTree size={48} className="mx-auto mb-3 text-slate-300" />
              <p className="font-medium">
                {searchQuery ? 'No subjects match your search' : 'No subjects yet'}
              </p>
              <p className="text-sm mt-1">
                {searchQuery ? 'Try a different search term' : 'Add subjects to organize your classes'}
              </p>
            </div>
          ) : (
            filteredSubjects.map((subject) => (
              <div
                key={subject.id}
                className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
                data-testid={`subject-row-${subject.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <FolderTree size={18} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{subject.name}</p>
                    {subject.created_at && (
                      <p className="text-xs text-slate-400">
                        Added {new Date(subject.created_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
                
                {canManage && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEdit(subject)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit subject"
                      data-testid={`edit-subject-${subject.id}`}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(subject.id, subject.name)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete subject"
                      data-testid={`delete-subject-${subject.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
        
        {/* Footer */}
        {filteredSubjects.length > 0 && (
          <div className="px-4 py-3 bg-slate-50 text-sm text-slate-500">
            Showing {filteredSubjects.length} of {subjects.length} subjects
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">
                {editingSubject ? 'Edit Subject' : 'Add Subject'}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setFormName('');
                  setEditingSubject(null);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Subject Name *
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Mathematics, Hindi, General Science"
                  autoFocus
                  required
                  data-testid="subject-name-input"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setFormName('');
                    setEditingSubject(null);
                  }}
                  className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
                  data-testid="save-subject-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      {editingSubject ? 'Update' : 'Create'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subjects;
